/* eslint-disable no-unused-vars */
/* eslint-disable react/forbid-prop-types */
import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { format, addDays } from 'date-fns';
import zhTW from 'date-fns/locale/zh-TW';
import 'chartjs-adapter-date-fns';
import { Chart, registerables } from 'chart.js';
import Typography from '../../../../components/Typography/Typography';
import Divider from '../../../../components/Divider/Divider';
import Dialog from '../../../../components/Dialog/Dialog';
import DialogTitle from '../../../../components/DialogTitle/DialogTitle';
import DialogContent from '../../../../components/DialogContent/DialogContent';
import DialogActions from '../../../../components/DialogActions/DialogActions';
import CircularProgress from '../../../../components/CircularProgress/CircularProgress';
import useStyles from './useStyles';
import useCommonStyles from '../../useStyles';
import { parseDecimalToFixedString } from '../../../../Utils/stringUtil';

const SPCChartType = {
  AVG: 1,
  SD: 2,
};

const ChartDialog = ({ open, handleChartClose, currentRow, spcChartData, spcChartConfig }) => {
  const { material, productName, specItem } = currentRow;
  const [isLoading, setIsLoading] = useState(false);
  const [isAvgChartComplete, setIsAvgChartComplete] = useState(false);
  const [isSdChartComplete, setIsSdChartComplete] = useState(false);
  const avgSpcChartEl = useRef(null);
  const avgSpcChart = useRef(null);
  const sdSpcChartEl = useRef(null);
  const sdSpcChart = useRef(null);
  const { classes } = useStyles();
  const commonClasses = useCommonStyles().classes;
  Chart.register(...registerables);

  const {
    measureTarget,
    measureUpperSpec,
    measureLowerSpec,
    avgDetectValueMean,
    sdDetectValueMean,
  } = spcChartConfig;
  const { UCLAvg, LCLAvg, UCLSd, LCLSd } = currentRow;
  const upperSpec = +measureTarget + +measureUpperSpec;
  const lowerSpec = +measureTarget - +measureLowerSpec;
  const avgTargetArr = [+measureTarget, upperSpec, lowerSpec, UCLAvg, LCLAvg];
  const sdTargetArr = [UCLSd, LCLSd];

  const getLabels = () => Array.from(spcChartData, (item) => item.modifyDate);
  const autoGenerateLabels = () => [
    ...getLabels(),
    addDays(new Date(getLabels()[0]), 1).toISOString(),
  ];

  const getSourceArr = () => (spcChartData.length > 1 ? spcChartData : new Array(2));

  const drawSpcChart = (
    ctx,
    type,
    dataset,
    maxValue,
    minValue,
    UCL,
    LCL,
    targetData,
    upperSpecData,
    lowerSpecData
  ) =>
    new Chart(ctx, {
      data: {
        labels: getLabels().length > 1 ? getLabels() : autoGenerateLabels(),
        datasets:
          type === SPCChartType.AVG
            ? [
                {
                  type: 'line',
                  yAxisID: 'y',
                  data: dataset,
                  backgroundColor: ['rgba(1, 162, 153, 1)'],
                  borderColor: 'rgba(1, 162, 153, 1)',
                  pointRadius: dataset.length > 20 ? 1.5 : 3,
                  pointHoverRadius: dataset.length > 20 ? 3 : 5,
                  borderWidth: 2,
                  order: 2,
                },
                {
                  type: 'line',
                  yAxisID: 'y',
                  data: dataset,
                  backgroundColor: ['rgba(0,0,0,0)'],
                  borderColor: 'rgba(0,0,0,0)',
                  pointRadius: dataset.length > 20 ? 1.5 : 3,
                  pointHoverRadius: dataset.length > 20 ? 3 : 5,
                  borderWidth: 2,
                  order: 1,
                },
                {
                  type: 'line',
                  yAxisID: 'y',
                  data: targetData,
                  borderColor: ['rgba(63, 81, 181, 1)'],
                  borderDash: [4, 6],
                  pointRadius: 0,
                  borderWidth: 1.5,
                  order: 1,
                },
                {
                  type: 'line',
                  yAxisID: 'y',
                  data: upperSpecData,
                  borderColor: ['rgba(176, 0, 32, 1)'],
                  borderDash: [4, 6],
                  pointRadius: 0,
                  borderWidth: 1.5,
                  order: 1,
                },
                {
                  type: 'line',
                  yAxisID: 'y',
                  data: lowerSpecData,
                  borderColor: ['rgba(176, 0, 32, 1)'],
                  borderDash: [10, 10],
                  pointRadius: 0,
                  borderWidth: 1.5,
                  order: 1,
                },
                {
                  type: 'line',
                  yAxisID: 'y',
                  data: UCL,
                  borderColor: ['rgba(255, 152, 0, 1)'],
                  borderDash: [4, 6],
                  pointRadius: 0,
                  borderWidth: 1.5,
                  order: 1,
                },
                {
                  type: 'line',
                  yAxisID: 'y',
                  data: LCL,
                  borderColor: ['rgba(255, 152, 0, 1)'],
                  borderDash: [10, 10],
                  pointRadius: 0,
                  borderWidth: 1.5,
                  order: 1,
                },
              ]
            : [
                {
                  type: 'line',
                  yAxisID: 'y',
                  data: dataset,
                  backgroundColor: ['rgba(1, 162, 153, 1)'],
                  borderColor: 'rgba(1, 162, 153, 1)',
                  pointRadius: dataset.length > 20 ? 1.5 : 3,
                  pointHoverRadius: dataset.length > 20 ? 3 : 5,
                  borderWidth: 2,
                  order: 2,
                },
                {
                  type: 'line',
                  yAxisID: 'y',
                  data: dataset,
                  backgroundColor: ['rgba(0,0,0,0)'],
                  borderColor: 'rgba(0,0,0,0)',
                  pointRadius: dataset.length > 20 ? 1.5 : 3,
                  pointHoverRadius: dataset.length > 20 ? 3 : 5,
                  borderWidth: 2,
                  order: 1,
                },
                {
                  type: 'line',
                  yAxisID: 'y',
                  data: UCL,
                  borderColor: ['rgba(255, 152, 0, 1)'],
                  borderDash: [4, 6],
                  pointRadius: 0,
                  borderWidth: 1.5,
                  order: 1,
                },
                {
                  type: 'line',
                  yAxisID: 'y',
                  data: LCL,
                  borderColor: ['rgba(255, 152, 0, 1)'],
                  borderDash: [10, 10],
                  pointRadius: 0,
                  borderWidth: 1.5,
                  order: 1,
                },
              ],
      },
      options: {
        aspectRatio: 2.5,
        scales: {
          x: {
            axis: 'x',
            display: true,
            label: 'Defect Type',
            ticks: {
              autoSkipPadding: 5,
              maxRotation: 90,
              callback: (value, idx) => {
                if (getLabels().length <= 1) {
                  if (spcChartData[idx]) {
                    return `${format(new Date(autoGenerateLabels()[idx]), 'yyyy-MM-dd')} ${
                      spcChartData[idx].transId
                    }`;
                  }
                  return null;
                }
                if (getLabels().length <= 10 && getLabels().length > 1) {
                  return `${format(new Date(getLabels()[idx]), 'yyyy-MM-dd')} ${
                    spcChartData[idx].transId
                  }`;
                }
                return `${format(new Date(getLabels()[idx]), 'yyyy-MM-dd')}`;
              },
            },
            grid: {
              display: false,
            },
          },
          y: {
            suggestedMax: maxValue,
            suggestedMin: minValue,
            z: 2,
            grid: {
              drawBorder: false,
            },
            ticks: {
              padding: 25,
              stepSize: type === SPCChartType.AVG ? 0.05 : 0.01,
            },
          },
          //   y2: {
          //     type: 'linear',
          //     position: 'right',
          //     display: true,
          //     z: 1,
          //     grid: {
          //       display: false,
          //       drawBorder: false,
          //     },
          //     ticks: {
          //       display: false,
          //       // TODO: when the deviation is too big, will cause the performance issue -> use plan B to show those values under the chart if that is fine by client (2021.07.11)
          //       // stepSize: 0.01,
          //       // padding: 25,
          //       // callback: (value, idx, values) => {
          //       //   if (type === SPCChartType.AVG) {
          //       //     if (avgTargetArr.includes(value)) {
          //       //       return value;
          //       //     }
          //       //   }
          //       //   if (type === SPCChartType.SD) {
          //       //     if (sdTargetArr.includes(value)) {
          //       //       return value;
          //       //     }
          //       //   }
          //       //   return null;
          //       // },
          //     },
          //   },
          y2: {
            type: 'linear',
            position: 'right',
            display: true,
            z: 1,
            grid: {
              display: false,
              drawBorder: false,
            },
            afterDataLimits: (axis) => {
              // 確保 y2 軸與 y 軸的範圍完全相同
              const yAxis = axis.chart.scales.y;
              if (yAxis) {
                // eslint-disable-next-line no-param-reassign
                axis.min = yAxis.min;
                // eslint-disable-next-line no-param-reassign
                axis.max = yAxis.max;
              }
            },
            ticks: {
              padding: 10,
              // 使用與左側Y軸相同的刻度間距，確保刻度對齊
              stepSize: type === SPCChartType.AVG ? 0.05 : 0.01,
              callback: (value, index, ticks) => {
                // 定義要顯示的控制線值（帶優先順序）
                const controlValues = [];

                if (type === SPCChartType.AVG) {
                  controlValues.push(
                    { val: UCL[0], label: '', priority: 1 }, // UCL
                    { val: LCL[0], label: '', priority: 1 }, // LCL
                    { val: upperSpec, label: '', priority: 2 }, // Upper Spec
                    { val: lowerSpec, label: '', priority: 2 }, // Lower Spec
                    { val: +measureTarget, label: '', priority: 3 } // Target
                  );
                } else if (type === SPCChartType.SD) {
                  controlValues.push(
                    { val: UCL[0], label: '', priority: 1 }, // UCL
                    { val: LCL[0], label: '', priority: 1 } // LCL
                  );
                }

                // 使用固定的刻度間距
                const tickSpacing = type === SPCChartType.AVG ? 0.05 : 0.01;
                // ==================== 調整右側 y 軸顯示參數 ====================
                // tolerance: 容許誤差 - 控制線數值與刻度值的最大差距
                //   只在刻度值接近控制線值時才顯示（避免重複）
                //
                // minSpacing: 最小間距 - 兩個標籤之間的最小距離
                //   避免兩個控制線值太接近時重疊顯示
                // ============================================================
                // 容許誤差設定為刻度間距的一半，確保只有最接近的刻度會顯示
                const tolerance = tickSpacing * 0.5;
                // 最小間距設定為刻度間距，避免相鄰刻度都顯示標籤
                const minSpacing = tickSpacing;

                // 找到最接近的控制線
                let closestControl = null;
                let minDistance = Infinity;

                controlValues.forEach((control) => {
                  const distance = Math.abs(value - control.val);
                  if (distance < minDistance && distance < tolerance) {
                    minDistance = distance;
                    closestControl = control;
                  }
                });

                // 如果找到匹配的控制線，檢查是否要顯示（避免重疊）
                if (closestControl) {
                  // 檢查是否有其他更高優先順序的控制線在附近
                  const hasHigherPriorityNearby = controlValues.some((other) => {
                    if (other === closestControl) return false;
                    if (other.priority >= closestControl.priority) return false;
                    return Math.abs(other.val - closestControl.val) < minSpacing;
                  });

                  if (!hasHigherPriorityNearby) {
                    return `${closestControl.label} ${closestControl.val.toFixed(2)}`;
                  }
                }

                return '';
              },
            },
          },
        },
        plugins: {
          datalabels: {
            display: false,
          },
          legend: {
            display: false,
          },
          title: {
            display: true,
            text: type === SPCChartType.AVG ? '平均值SPC Chart' : '標準差SPC Chart',
            font: {
              size: 16,
              weight: 'normal',
            },
            padding: {
              bottom: 30,
              top: 24,
            },
          },
          tooltip: {
            filter: (tooltipItem) => tooltipItem.datasetIndex === 0,
            callbacks: {
              label: (context) => {
                if (!context) return null;
                const { raw } = context;
                return type === SPCChartType.AVG
                  ? `Avg: ${parseDecimalToFixedString(raw)}`
                  : `SD: ${parseDecimalToFixedString(raw)}`;
              },
              beforeTitle: (context) => {
                if (!context[0]) return null;
                const { dataIndex } = context[0];
                return `移轉單號：${spcChartData[dataIndex].transId}`;
              },
              title: (context) => {
                if (!context[0]) return null;
                const { label } = context[0];
                return `檢測日期：${format(new Date(label), 'yyyy-MM-dd')}`;
              },
              afterTitle: (context) => {
                if (!context[0]) return null;
                const { label } = context[0];
                return `檢驗時間：${format(new Date(label), 'HH:mm:ss')}`;
              },
              labelColor: () => ({
                borderRadius: 6,
                borderColor: 'rgba(0,0,0,0)',
                backgroundColor: 'rgba(1, 162, 153, 1)',
              }),
            },
          },
        },
        animation: {
          onComplete: (e) => {
            if (isAvgChartComplete && isSdChartComplete) return;
            if (!isAvgChartComplete && type === SPCChartType.AVG) {
              setIsAvgChartComplete(true);
            }
            if (!isSdChartComplete && type === SPCChartType.SD) {
              setIsSdChartComplete(true);
            }
          },
        },
      },
    });

  const getMaxValue = (type) => {
    const UCLValue =
      type === SPCChartType.AVG
        ? parseFloat(
            parseDecimalToFixedString(
              +spcChartConfig.measureTarget + +spcChartConfig.measureUpperSpec
            )
          )
        : parseFloat(parseDecimalToFixedString(currentRow.UCLSd));
    const datasetMax =
      type === SPCChartType.AVG
        ? Math.max(...spcChartData.map((item) => item.avgDetectValue))
        : Math.max(...spcChartData.map((item) => item.sdDetectValue));
    return datasetMax > UCLValue
      ? datasetMax + (type === SPCChartType.AVG ? 0.1 : 0.02)
      : UCLValue + (type === SPCChartType.AVG ? 0.1 : 0.02);
  };

  const getMinValue = (type) => {
    const LCLValue =
      type === SPCChartType.AVG
        ? parseFloat(
            parseDecimalToFixedString(
              +spcChartConfig.measureTarget - +spcChartConfig.measureLowerSpec
            )
          )
        : parseFloat(parseDecimalToFixedString(currentRow.LCLSd));
    const datasetMin =
      type === SPCChartType.AVG
        ? Math.min(...spcChartData.map((item) => item.avgDetectValue))
        : Math.min(...spcChartData.map((item) => item.sdDetectValue));
    const minValue =
      datasetMin < LCLValue
        ? datasetMin - (type === SPCChartType.AVG ? 0.1 : 0.02)
        : LCLValue - (type === SPCChartType.AVG ? 0.1 : 0.02);
    return minValue < 0 ? 0 : minValue;
  };

  const drawAvgSpcChart = () => {
    if (avgSpcChart.current) {
      avgSpcChart.current.destroy();
    }
    avgSpcChartEl.current = document.getElementById('avgSpcChartEl');
    const ctx = avgSpcChartEl.current.getContext('2d');
    const dataset = Array.from(spcChartData, (item) => item.avgDetectValue);
    const sourceArr = getSourceArr();
    const targetData = Array.from(sourceArr, () => spcChartConfig.measureTarget);
    const upperSpecData = Array.from(sourceArr, () =>
      parseFloat(
        parseDecimalToFixedString(+spcChartConfig.measureTarget + +spcChartConfig.measureUpperSpec)
      )
    );
    const lowerSpecData = Array.from(sourceArr, () =>
      parseFloat(
        parseDecimalToFixedString(+spcChartConfig.measureTarget - +spcChartConfig.measureLowerSpec)
      )
    );
    const UCLData = Array.from(sourceArr, () =>
      parseFloat(parseDecimalToFixedString(currentRow.UCLAvg))
    );
    const LCLData = Array.from(sourceArr, () =>
      parseFloat(parseDecimalToFixedString(currentRow.LCLAvg))
    );
    const type = SPCChartType.AVG;
    const maxValue = getMaxValue(type);
    const minValue = getMinValue(type);
    avgSpcChart.current = drawSpcChart(
      ctx,
      type,
      dataset.length > 1 ? dataset : [...dataset, ...dataset],
      maxValue,
      minValue,
      UCLData,
      LCLData,
      targetData,
      upperSpecData,
      lowerSpecData
    );
  };

  const drawSdSpcChart = () => {
    if (sdSpcChart.current) {
      sdSpcChart.current.destroy();
    }
    sdSpcChartEl.current = document.getElementById('sdSpcChartEl');
    const ctx = sdSpcChartEl.current.getContext('2d');
    const dataset = Array.from(spcChartData, (item) => item.sdDetectValue);
    const sourceArr = getSourceArr();
    const UCLData = Array.from(sourceArr, () =>
      parseFloat(parseDecimalToFixedString(currentRow.UCLSd))
    );
    const LCLData = Array.from(sourceArr, () =>
      parseFloat(parseDecimalToFixedString(currentRow.LCLSd))
    );
    const type = SPCChartType.SD;
    const maxValue = getMaxValue(type);
    const minValue = getMinValue(type);
    avgSpcChart.current = drawSpcChart(ctx, type, dataset, maxValue, minValue, UCLData, LCLData);
  };

  useEffect(() => {
    if (!open) {
      setIsAvgChartComplete(false);
      setIsSdChartComplete(false);
      return;
    }
    setIsLoading(true);
    setTimeout(() => {
      drawAvgSpcChart();
      drawSdSpcChart();
    }, 100);
  }, [open]);

  useEffect(() => {
    if (isAvgChartComplete && isSdChartComplete) {
      setIsLoading(false);
    }
  }, [isAvgChartComplete, isSdChartComplete]);

  useEffect(
    () => () => {
      if (avgSpcChart.current) {
        avgSpcChart.current.destroy();
      }
      if (sdSpcChart.current) {
        sdSpcChart.current.destroy();
      }
    },
    []
  );

  return (
    <div className={classes.dialog}>
      <Dialog open={open} onClose={handleChartClose} fullWidth maxWidth="lg">
        <DialogTitle
          id="detail-title"
          onClose={handleChartClose}
          className={commonClasses.detail_header}
        >
          {material}｜{productName}｜{specItem} SPC Chart
        </DialogTitle>
        <DialogContent dividers className={commonClasses.detail_content}>
          <div className={`${classes.chart_container} ${isLoading ? classes.chart_loading : ''}`}>
            <canvas id="avgSpcChartEl" width="600" height="400" />
            {!isLoading && (
              <div className={classes.chart_legend_container}>
                <div className={classes.chart_legend}>
                  <span className={`${classes.chart_legend_label} ucl`} />
                  <Typography variant="subtitle2">UCL: {UCLAvg.toFixed(2)}</Typography>
                </div>
                <div className={classes.chart_legend}>
                  <span className={`${classes.chart_legend_label} lcl`} />
                  <Typography variant="subtitle2">LCL: {LCLAvg.toFixed(2)}</Typography>
                </div>
                <div className={classes.chart_legend}>
                  <span className={`${classes.chart_legend_label} upperSpec`} />
                  <Typography variant="subtitle2">Upper Spec: {upperSpec.toFixed(2)}</Typography>
                </div>
                <div className={classes.chart_legend}>
                  <span className={`${classes.chart_legend_label} lowerSpec`} />
                  <Typography variant="subtitle2">Lower Spec: {lowerSpec.toFixed(2)}</Typography>
                </div>
                <div className={classes.chart_legend}>
                  <span className={`${classes.chart_legend_label} target`} />
                  <Typography variant="subtitle2">Target: {measureTarget}</Typography>
                </div>
                <div className={classes.chart_legend}>
                  <span className={`${classes.chart_legend_label} mean`} />
                  <Typography variant="subtitle2">Mean: {avgDetectValueMean.toFixed(2)}</Typography>
                </div>
              </div>
            )}
          </div>
          {!isLoading && (
            <Divider className={commonClasses.dashed_divider} style={{ margin: `30px auto` }} />
          )}
          <div className={`${classes.chart_container} ${isLoading ? classes.chart_loading : ''}`}>
            <canvas id="sdSpcChartEl" width="600" height="400" />
            {!isLoading && (
              <div className={classes.chart_legend_container}>
                <div className={classes.chart_legend}>
                  <span className={`${classes.chart_legend_label} ucl`} />
                  <Typography variant="subtitle2">UCL: {UCLSd}</Typography>
                </div>
                <div className={classes.chart_legend}>
                  <span className={`${classes.chart_legend_label} lcl`} />
                  <Typography variant="subtitle2">LCL: {LCLSd}</Typography>
                </div>
                <div className={classes.chart_legend}>
                  <span className={`${classes.chart_legend_label} mean`} />
                  <Typography variant="subtitle2">Mean: {sdDetectValueMean.toFixed(2)}</Typography>
                </div>
              </div>
            )}
          </div>
          {isLoading && (
            <CircularProgress direction="column" alignItems="center" mt={11.25}>
              圖表繪製中⋯⋯
            </CircularProgress>
          )}
        </DialogContent>
        <DialogActions className={commonClasses.detail_footer} onClick={handleChartClose}>
          確認
        </DialogActions>
      </Dialog>
    </div>
  );
};

ChartDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  handleChartClose: PropTypes.func.isRequired,
  currentRow: PropTypes.object.isRequired,
  spcChartData: PropTypes.array.isRequired,
  spcChartConfig: PropTypes.object.isRequired,
};

export default ChartDialog;
